/**
 * The Staffing Request form — ask Resource Management to Replace, Add, Remove or Swap
 * someone on a Solution's Pod. Nothing on any Pod changes until an RM approves.
 *
 * A Swap moves someone from another Pod into a seat here, filled or open. It can be opened
 * pre-filled (`initial`) from an open role on a plan.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useDisciplines } from "@/hooks/use-disciplines";
import {
  useCreateStaffingRequest,
  useStaffingRequestPriorities,
  useStaffingSolutionOptions,
} from "@/hooks/use-staffing-requests";
import { addSeatOptions, type SeatRole } from "@/lib/staffing-change";
import { cn } from "@/lib/utils";
import {
  type CreateStaffingRequestInput,
  STAFFING_REQUEST_KINDS,
  type StaffingResourceOption,
  type StaffingSolutionOption,
} from "@/schemas/staffing-requests";
import { ResourceOptionPicker } from "./ResourceOptionPicker";
import { addSeatLabel, compositionLabel, KIND_DESCRIPTIONS, seatLabel } from "./request-copy";
import { SwapCandidatePicker, type SwapChoice } from "./SwapCandidatePicker";

/** Radix SelectItem forbids an empty-string value — this sentinel maps back to "none". */
const NONE = "__none__";

// The order the form offers the kinds in — Replace is the most common ask.
const KIND_ORDER = ["Replace", "Add", "Remove", "Swap"] as const;

// A Swap's seat is either a Pod member's (`member:<assignmentId>`) or an open one
// (`open:<role>`) — one select covers both.
const MEMBER_SEAT = "member:";
const OPEN_SEAT = "open:";

const formSchema = z
  .object({
    solutionId: z.string().min(1, "Choose a Solution"),
    kind: z.enum(STAFFING_REQUEST_KINDS),
    targetAssignmentId: z.string(),
    seat: z.string(),
    swapSeat: z.string(),
    sourceAssignmentId: z.string(),
    disciplineId: z.string(),
    suggestedResourceId: z.string(),
    priorityId: z.string(),
    neededBy: z.string(),
    reason: z
      .string()
      .trim()
      .min(1, "Say why the change is needed")
      .max(2000, "Keep the reason under 2,000 characters"),
  })
  .superRefine((v, ctx) => {
    if (v.kind === "Add" && !v.seat) {
      ctx.addIssue({ code: "custom", path: ["seat"], message: "Choose a seat" });
    }
    if (v.kind === "Swap") {
      if (!v.swapSeat) {
        ctx.addIssue({ code: "custom", path: ["swapSeat"], message: "Choose a seat" });
      }
      if (!v.sourceAssignmentId) {
        ctx.addIssue({
          code: "custom",
          path: ["sourceAssignmentId"],
          message: "Choose who moves in",
        });
      }
    }
    if ((v.kind === "Replace" || v.kind === "Remove") && !v.targetAssignmentId) {
      ctx.addIssue({
        code: "custom",
        path: ["targetAssignmentId"],
        message: "Choose who on the Pod",
      });
    }
  });

type FormValues = z.infer<typeof formSchema>;

const EMPTY: FormValues = {
  solutionId: "",
  kind: "Replace",
  targetAssignmentId: "",
  seat: "",
  swapSeat: "",
  sourceAssignmentId: "",
  disciplineId: NONE,
  suggestedResourceId: "",
  priorityId: NONE,
  neededBy: "",
  reason: "",
};

function orNull(value: string): string | null {
  return value === "" || value === NONE ? null : value;
}

function toPayload(
  v: FormValues,
  solution: StaffingSolutionOption,
): CreateStaffingRequestInput | null {
  const common = {
    solutionId: v.solutionId,
    priorityId: orNull(v.priorityId),
    neededBy: orNull(v.neededBy),
    reason: v.reason,
  };
  if (v.kind === "Remove") {
    return { kind: "Remove", ...common, targetAssignmentId: v.targetAssignmentId };
  }
  if (v.kind === "Swap") {
    const seat = resolveSwapSeat(v.swapSeat, solution);
    return seat
      ? {
          kind: "Swap",
          ...common,
          role: seat.role,
          targetAssignmentId: seat.targetAssignmentId,
          sourceAssignmentId: v.sourceAssignmentId,
          disciplineId: orNull(v.disciplineId),
        }
      : null;
  }
  const choice = {
    disciplineId: orNull(v.disciplineId),
    suggestedResourceId: orNull(v.suggestedResourceId),
  };
  if (v.kind === "Replace") {
    return { kind: "Replace", ...common, ...choice, targetAssignmentId: v.targetAssignmentId };
  }
  const seat = addSeatOptions(solution.pod).find((o) => o.role === v.seat);
  return seat ? { kind: "Add", ...common, ...choice, role: seat.role } : null;
}

/** The seat a Swap select value names, checked against the Pod as the form sees it. */
function resolveSwapSeat(
  value: string,
  solution: StaffingSolutionOption,
): { role: SeatRole; targetAssignmentId: string | null } | null {
  if (value.startsWith(MEMBER_SEAT)) {
    const id = value.slice(MEMBER_SEAT.length);
    const member = solution.pod.members.find((m) => m.assignmentId === id);
    return member ? { role: member.role, targetAssignmentId: member.assignmentId } : null;
  }
  const option = addSeatOptions(solution.pod).find((o) => `${OPEN_SEAT}${o.role}` === value);
  return option ? { role: option.role, targetAssignmentId: null } : null;
}

/**
 * The Swap seat value for a plan role's seat: the Pod member in it when filled (Supporting
 * has many, so it stays open), else that open seat — or nothing if the Pod can't take it.
 */
function swapSeatFor(role: SeatRole | null, solution: StaffingSolutionOption): string {
  if (role === null) return "";
  if (role !== "Supporting") {
    const member = solution.pod.members.find((m) => m.role === role);
    if (member) return `${MEMBER_SEAT}${member.assignmentId}`;
  }
  return addSeatOptions(solution.pod).some((o) => o.role === role) ? `${OPEN_SEAT}${role}` : "";
}

/** Opens the form as a Swap on this Solution, with the seat chosen when it can be. */
export interface SwapPrefill {
  solutionId: string;
  seat: SeatRole | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: SwapPrefill | null;
}

export function NewStaffingRequestDialog({ open, onOpenChange, initial = null }: Props) {
  const create = useCreateStaffingRequest();
  const solutions = useStaffingSolutionOptions(open);
  const { data: disciplines = [] } = useDisciplines();
  const { data: priorities = [] } = useStaffingRequestPriorities();
  const [suggested, setSuggested] = useState<StaffingResourceOption | null>(null);
  const [swapChoice, setSwapChoice] = useState<SwapChoice | null>(null);

  const form = useForm<FormValues>({ resolver: zodResolver(formSchema), defaultValues: EMPTY });
  const solutionId = form.watch("solutionId");
  const kind = form.watch("kind");
  const targetAssignmentId = form.watch("targetAssignmentId");
  const solution = solutions.data?.find((s) => s.id === solutionId) ?? null;
  const target = solution?.pod.members.find((m) => m.assignmentId === targetAssignmentId) ?? null;

  // Apply a pre-fill once per opening, as soon as the Solutions (and their Pods) load.
  const applied = useRef<SwapPrefill | null>(null);
  useEffect(() => {
    // Wait for a fresh fetch: a cached list from an earlier opening may predate the Pod.
    if (!open || !initial || applied.current === initial) return;
    if (!solutions.data || solutions.isFetching) return;
    applied.current = initial;
    const target = solutions.data.find((s) => s.id === initial.solutionId);
    form.reset({
      ...EMPTY,
      solutionId: target?.id ?? "",
      kind: "Swap",
      swapSeat: target ? swapSeatFor(initial.seat, target) : "",
    });
    setSuggested(null);
    setSwapChoice(null);
  }, [open, initial, solutions.data, solutions.isFetching, form]);

  function close(next: boolean) {
    if (!next) {
      form.reset(EMPTY);
      setSuggested(null);
      setSwapChoice(null);
      applied.current = null;
    }
    onOpenChange(next);
  }

  /** Changing Solution or kind invalidates who / which seat / who to suggest. */
  function clearSeat() {
    form.setValue("targetAssignmentId", "");
    form.setValue("seat", "");
    form.setValue("suggestedResourceId", "");
    form.setValue("swapSeat", "");
    form.setValue("sourceAssignmentId", "");
    setSuggested(null);
    setSwapChoice(null);
  }

  function pickSwap(choice: SwapChoice | null) {
    setSwapChoice(choice);
    form.setValue("sourceAssignmentId", choice?.seat.assignmentId ?? "", {
      shouldValidate: choice !== null,
    });
  }

  function pickSuggested(option: StaffingResourceOption | null) {
    setSuggested(option);
    form.setValue("suggestedResourceId", option?.id ?? "");
  }

  function onSubmit(values: FormValues) {
    if (!solution) return;
    const payload = toPayload(values, solution);
    if (!payload) {
      form.setError(values.kind === "Swap" ? "swapSeat" : "seat", { message: "Choose a seat" });
      return;
    }
    create.mutate(payload, { onSuccess: () => close(false) });
  }

  const solutionOptions = (solutions.data ?? []).map((s) => ({
    value: s.id,
    label: s.name,
    group: s.accountName,
  }));
  const activePriorities = priorities.filter((p) => p.active);
  const activeDisciplines = disciplines.filter((d) => d.active);
  const memberOptions = solution?.pod.members ?? [];

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>New staffing request</DialogTitle>
          <DialogDescription>
            Resource Management reviews every request. Nothing on the Pod changes until it is
            approved.
          </DialogDescription>
        </DialogHeader>

        {solutions.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : solutions.isError ? (
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>Could not load your Solutions.</p>
            <Button variant="outline" size="sm" onClick={() => solutions.refetch()}>
              Retry
            </Button>
          </div>
        ) : solutionOptions.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            You have no open Solutions to request a change on. ACCT users can request changes on
            Accounts they own; SA users on Solutions they are a Primary on; RM and Admin on any
            Solution.
          </p>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="min-w-0 space-y-4">
              <FormField
                control={form.control}
                name="solutionId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Solution</FormLabel>
                    <FormControl>
                      <SearchableSelect
                        options={solutionOptions}
                        value={field.value}
                        onValueChange={(v) => {
                          field.onChange(v);
                          clearSeat();
                        }}
                        placeholder="Choose a Solution"
                        searchPlaceholder="Search Solutions"
                        className="min-w-0"
                      />
                    </FormControl>
                    {solution && (
                      <FormDescription>
                        {compositionLabel(solution.pod.composition)} ·{" "}
                        {solution.pod.members.length === 0
                          ? "nobody assigned yet"
                          : solution.pod.members
                              .map(
                                (m) =>
                                  `${seatLabel(m.role, solution.pod.positionDisciplines)} ${m.resourceName}`,
                              )
                              .join(", ")}
                      </FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="kind"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>What should change?</FormLabel>
                    <fieldset className="grid min-w-0 gap-2 sm:grid-cols-2">
                      <legend className="sr-only">Request kind</legend>
                      {KIND_ORDER.map((k) => (
                        <label
                          key={k}
                          className={cn(
                            "cursor-pointer rounded-md border px-3 py-2 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
                            field.value === k
                              ? "border-primary bg-primary/10"
                              : "border-border bg-card hover:border-foreground/40",
                          )}
                        >
                          <input
                            type="radio"
                            name="staffing-request-kind"
                            value={k}
                            checked={field.value === k}
                            onChange={() => {
                              field.onChange(k);
                              clearSeat();
                            }}
                            className="sr-only"
                          />
                          <span className="block text-sm font-semibold">{k}</span>
                          <span className="block text-xs text-muted-foreground">
                            {KIND_DESCRIPTIONS[k]}
                          </span>
                        </label>
                      ))}
                    </fieldset>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {solution && (kind === "Replace" || kind === "Remove") && (
                <FormField
                  control={form.control}
                  name="targetAssignmentId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {kind === "Replace"
                          ? "Who is being replaced?"
                          : "Who should come off the Pod?"}
                      </FormLabel>
                      {memberOptions.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          Nobody is on this Pod yet — use Add instead.
                        </p>
                      ) : (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Choose a Pod member" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {memberOptions.map((m) => {
                              const pending = solution.pendingTargetAssignmentIds.includes(
                                m.assignmentId,
                              );
                              return (
                                <SelectItem
                                  key={m.assignmentId}
                                  value={m.assignmentId}
                                  disabled={pending}
                                >
                                  {m.resourceName} (
                                  {seatLabel(m.role, solution.pod.positionDisciplines)})
                                  {pending && " — request already pending"}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {solution && kind === "Add" && (
                <FormField
                  control={form.control}
                  name="seat"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Which seat?</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Choose a seat" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {addSeatOptions(solution.pod).map((o) => (
                            <SelectItem key={o.role} value={o.role}>
                              {addSeatLabel(o, solution.pod.positionDisciplines)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {solution && kind === "Swap" && (
                <>
                  <FormField
                    control={form.control}
                    name="swapSeat"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Which seat?</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Choose a seat" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {memberOptions.map((m) => {
                              const pending = solution.pendingTargetAssignmentIds.includes(
                                m.assignmentId,
                              );
                              return (
                                <SelectItem
                                  key={m.assignmentId}
                                  value={`${MEMBER_SEAT}${m.assignmentId}`}
                                  disabled={pending}
                                >
                                  {seatLabel(m.role, solution.pod.positionDisciplines)} —{" "}
                                  {m.resourceName}
                                  {pending && " (request already pending)"}
                                </SelectItem>
                              );
                            })}
                            {addSeatOptions(solution.pod).map((o) => (
                              <SelectItem key={o.role} value={`${OPEN_SEAT}${o.role}`}>
                                {addSeatLabel(o, solution.pod.positionDisciplines)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Whoever is in the seat comes off the Pod when the swap is approved.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="sourceAssignmentId"
                    render={() => (
                      <FormItem>
                        <FormLabel htmlFor="swap-candidate">Who moves in?</FormLabel>
                        <SwapCandidatePicker
                          id="swap-candidate"
                          solutionId={solution.id}
                          selected={swapChoice}
                          onChange={pickSwap}
                        />
                        <FormDescription>
                          {swapChoice
                            ? `${swapChoice.candidate.name} stays on ${swapChoice.seat.solution.name} until the swap is approved; then that seat opens up.`
                            : "Someone currently on another Solution's Pod."}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              {solution && kind !== "Remove" && (
                <>
                  <FormField
                    control={form.control}
                    name="disciplineId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Discipline needed</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value={NONE}>No preference</SelectItem>
                            {activeDisciplines.map((d) => (
                              <SelectItem key={d.id} value={d.id}>
                                {d.name} ({d.code})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {kind !== "Swap" && (
                    <FormItem>
                      <FormLabel htmlFor="suggested-resource">Suggest someone (optional)</FormLabel>
                      <ResourceOptionPicker
                        id="suggested-resource"
                        solutionId={solution.id}
                        selected={suggested}
                        onChange={pickSuggested}
                        excludeIds={solution.pod.members.map((m) => m.resourceId)}
                      />
                      <FormDescription>
                        Resource Management confirms the suggestion or chooses someone else.
                        {target && ` ${target.resourceName} stays on the Pod until it is approved.`}
                      </FormDescription>
                    </FormItem>
                  )}
                </>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="priorityId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Priority</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={NONE}>None</SelectItem>
                          {activePriorities.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="neededBy"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Needed by (optional)</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Why is this needed?</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={3}
                        placeholder="e.g. Client asked for a Delivery Manager with healthcare experience from next sprint"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => close(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={create.isPending}>
                  {create.isPending ? "Sending…" : "Send request"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
